import { formatMgrs, parseMgrs } from '../shared/grid';
import { el } from './ui';

export interface PlaceValue {
  lat: string;
  lng: string;
  accuracy: string;
  description: string;
}

export function emptyPlace(): PlaceValue {
  return { lat: '', lng: '', accuracy: '', description: '' };
}

export function locationField(initial: PlaceValue = emptyPlace()): {
  element: HTMLElement;
  get: () => PlaceValue;
  set: (value: PlaceValue) => void;
} {
  let value: PlaceValue = { ...initial };
  let gridInvalid = false;

  const status = el('div', { className: 'loc-status' });
  const accuracy = el('span', { className: 'loc-accuracy' });
  const capture = el('button', {
    type: 'button',
    className: 'btn btn-primary',
    textContent: 'Capture my location',
  });
  const clear = el('button', {
    type: 'button',
    className: 'btn btn-ghost',
    textContent: 'Clear location',
  });
  const desc = el('input', {
    className: 'input',
    placeholder: 'Landmark or road if GPS fails',
    value: value.description,
  }) as HTMLInputElement;
  const grid = el('input', {
    className: 'input',
    placeholder: 'MGRS, e.g. 4QFJ 03584 71053',
    autocapitalize: 'characters',
    spellcheck: 'false',
  }) as HTMLInputElement;

  function gridText(): string {
    const latN = Number(value.lat);
    const lngN = Number(value.lng);
    if (!Number.isFinite(latN) || !Number.isFinite(lngN) || value.lat === '' || value.lng === '') {
      return '';
    }
    return formatMgrs(latN, lngN);
  }

  function paint(): void {
    const text = gridText();
    const accN = value.accuracy === '' ? NaN : Number(value.accuracy);
    if (text && !gridInvalid) {
      status.textContent = text;
      status.className = 'loc-status ok';
      grid.value = text;
      clear.hidden = false;
      if (Number.isFinite(accN)) {
        accuracy.hidden = false;
        accuracy.textContent =
          accN <= 25 ? 'Good fix' : accN <= 100 ? 'Fair fix' : 'Coarse fix';
        accuracy.className =
          'loc-accuracy ' +
          (accN <= 25 ? 'good' : accN <= 100 ? 'fair' : 'coarse');
      } else {
        accuracy.hidden = true;
      }
    } else if (!gridInvalid) {
      status.textContent = 'No GPS fix yet';
      status.className = 'loc-status';
      grid.value = '';
      accuracy.hidden = true;
      clear.hidden = true;
    }
    desc.value = value.description;
  }

  function commitGrid(): void {
    const raw = grid.value.trim();
    if (!raw) {
      gridInvalid = false;
      value = { ...value, lat: '', lng: '', accuracy: '' };
      paint();
      return;
    }
    const parsed = parseMgrs(raw);
    if (!parsed) {
      gridInvalid = true;
      status.textContent = 'That grid is not valid';
      status.className = 'loc-status bad';
      return;
    }
    gridInvalid = false;
    value = {
      ...value,
      lat: parsed.lat.toFixed(6),
      lng: parsed.lng.toFixed(6),
      accuracy: '',
    };
    paint();
  }

  capture.addEventListener('click', () => {
    if (!navigator.geolocation) {
      status.textContent = 'No GPS on this phone — describe the place';
      status.className = 'loc-status bad';
      return;
    }
    capture.disabled = true;
    capture.textContent = 'Getting GPS…';
    status.textContent = 'Keep the screen on…';
    status.className = 'loc-status';
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        gridInvalid = false;
        value = {
          ...value,
          lat: pos.coords.latitude.toFixed(6),
          lng: pos.coords.longitude.toFixed(6),
          accuracy: String(Math.round(pos.coords.accuracy)),
        };
        capture.disabled = false;
        capture.textContent = 'Capture my location';
        paint();
      },
      () => {
        capture.disabled = false;
        capture.textContent = 'Capture my location';
        status.textContent = 'GPS failed — describe the place or type a grid';
        status.className = 'loc-status bad';
      },
      { enableHighAccuracy: true, timeout: 20000, maximumAge: 5000 }
    );
  });

  clear.addEventListener('click', () => {
    gridInvalid = false;
    value = { ...value, lat: '', lng: '', accuracy: '' };
    paint();
  });
  desc.addEventListener('input', () => {
    value = { ...value, description: desc.value };
  });
  grid.addEventListener('change', () => commitGrid());

  const advanced = el('details', { className: 'advanced' }, [
    el('summary', { textContent: 'Edit grid' }),
    grid,
  ]);

  const element = el('div', { className: 'block' }, [
    el('span', { className: 'field-label', textContent: 'Place' }),
    capture,
    el('div', { className: 'loc-row' }, [status, accuracy]),
    clear,
    desc,
    advanced,
  ]);
  paint();

  return {
    element,
    get: () => {
      if (grid.value.trim() !== gridText()) commitGrid();
      if (gridInvalid) return { ...value, lat: '', lng: '', accuracy: '', description: desc.value.trim() };
      return { ...value, description: desc.value.trim() };
    },
    set: (next) => {
      gridInvalid = false;
      value = { ...next };
      paint();
    },
  };
}
